import translator from '@Components/Utilities/Translator';
import SelectField from '@Components/Shared/Controls/SelectField';
import TextField from '@Components/Shared/Controls/TextField';
import * as CurrencyFormatter from '@Components/Utilities/CurrencyFormatter';import { useState, useRef, useEffect } from 'react';
import { Slider, Box, Typography } from '@mui/material';
import { loadable } from '@Components/Utilities/SandhillsLoadable';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { useRecoilValue } from 'recoil';


const LoanApprovalEstimateBanner = loadable(() => import('@Components/Shared/LoanApprovalEstimate/LoanApprovalEstimateBanner'));
const LoanApprovalEstimateFinCalc = loadable(() => import('@Components/Shared/FinancialCalculator/LoanApprovalEstimateFinCalc'));

const CalculatorForm = (props) => {
    const currentSite = useRecoilValue(atoms.currentWebsite)
    const inputReferences = useRef({});
    let unformattedPrice = CurrencyFormatter.UnformatCurrency(props.Price, props.DefaultCultureCode, props.DefaultCurrency);
    unformattedPrice = unformattedPrice && unformattedPrice > 0 ? unformattedPrice : 1000;
    const isRVU = currentSite?.Site?.IsRVUniverse || false;

    const [paymentLabel, setPaymentLabel] = useState(translator.translate('sandhills.yourestimatedmonthlypayment').toLowerCase());

    const [formData, setFormData] = useState({
        paymentFrequency: '12',
        currency: props.DefaultCurrency || 'USD',
        cultureCode: props.DefaultCultureCode || "en-US",
        price: (unformattedPrice && !isNaN(unformattedPrice)) ? CurrencyFormatter.FormatCurrency(unformattedPrice, props.DefaultCultureCode || "en-US", props.DefaultCurrency || 'USD') : '',
        termYears: props.HideCurrencySelector && props.PaymentsAsLowAsTerm && props.PaymentAsLowAsTerm > 0 ? props.PaymentsAsLowAsTerm / 12 : '5',
        interest: '0%',
        downPayment: CurrencyFormatter.FormatCurrency(0, props.DefaultCultureCode || "en-US", props.DefaultCurrency || 'USD', 0, 0)
    });

    useEffect(() => {
        calculateFields();
    }, [formData])

    const getPaymentFrequencyOptions = () => {
        return [
            { Value: '12', Text: translator.translate('sandhills.rental.monthly') },
            { Value: '4', Text: translator.translate('sandhills.tradepubwebsite.mvc.quarterly') },
            { Value: '2', Text: translator.translate('sandhills.tradepubwebsite.mvc.biannually') },
            { Value: '1', Text: translator.translate('sandhills.tradepubwebsite.mvc.annually') }
        ];
    };

    const getLoanTermOptions = () => {
        let options = [
            { Value: '1', Text: '1 Year' },
            { Value: '2', Text: '2 Years' },
            { Value: '3', Text: '3 Years' },
            { Value: '4', Text: '4 Years' },
            { Value: '5', Text: '5 Years' },
            { Value: '6', Text: '6 Years' }]

        if (props.ShowExtendedLoanTerms) {
            options = [...options,
            { Value: '10', Text: '10 Years' },
            { Value: '15', Text: '15 Years' },
            { Value: '20', Text: '20 Years' },
            ];
        }

        return options;
    }

    const [calculatedFields, setCalculatedFields] = useState({
        payments: '',
        amountFinanced: '',
        totalAmount: '',
        financeCharge: ''
    });

    const calculateFields = () => {
        let amtFinanced = parseFloat(CurrencyFormatter.UnformatCurrency(formData.price, formData.cultureCode, formData.currency) - CurrencyFormatter.UnformatCurrency(formData.downPayment, formData.cultureCode, formData.currency));
        let totalPayments = parseInt(formData.termYears) * parseInt(formData.paymentFrequency);
        let interest = Number(CurrencyFormatter.UnformatCurrency(formData.interest.replace('%', ''), formData.cultureCode, formData.currency)) || 0;
        let calculatedRate = (interest / 100) / parseInt(formData.paymentFrequency);

        let payments, totalAmount, financeCharge;

        if (interest === 0) {
            payments = amtFinanced / totalPayments;
            totalAmount = amtFinanced;
            financeCharge = 0;
        } else {
            payments = (calculatedRate * amtFinanced) / (1 - (Math.pow((1 + calculatedRate), -totalPayments)));
            totalAmount = payments * totalPayments;
            financeCharge = totalAmount - amtFinanced;
        }

        setCalculatedFields({
            payments: CurrencyFormatter.FormatCurrency(payments, formData.cultureCode, formData.currency, null),
            amountFinanced: CurrencyFormatter.FormatCurrency(amtFinanced, formData.cultureCode, formData.currency),
            totalAmount: CurrencyFormatter.FormatCurrency(totalAmount, formData.cultureCode, formData.currency),
            financeCharge: CurrencyFormatter.FormatCurrency(financeCharge, formData.cultureCode, formData.currency)
        });
    }

    const updateForm = (e, formatCurrency, formatPercentage, keepDecimals) => {
        let name = e.target.name;
        let value = e.target.value;

        if (formatCurrency) {
            if (value && value !== '') {
                value = CurrencyFormatter.SwapCurrencyFormat(CurrencyFormatter.UnformatCurrency(value, formData.cultureCode, formData.currency), formData.cultureCode, formData.currency, formData.cultureCode, formData.currency, keepDecimals ? null : 0, keepDecimals ? null : 0, false);
            }

            if (value == '') {
                if (formData[name] && formData[name] !== '') {
                    value = CurrencyFormatter.SwapCurrencyFormat(CurrencyFormatter.UnformatCurrency(formData[name], formData.cultureCode, formData.currency), formData.cultureCode, formData.currency, formData.cultureCode, formData.currency, keepDecimals ? null : 0, keepDecimals ? null : 0, false);
                }
                value = CurrencyFormatter.SwapCurrencyFormat(CurrencyFormatter.UnformatCurrency('$0.00', formData.cultureCode, formData.currency), formData.cultureCode, formData.currency, formData.cultureCode, formData.currency, keepDecimals ? null : 0, keepDecimals ? null : 0, false);
            }
        }

        if (formatPercentage) {
            value = CurrencyFormatter.SwapCurrencyFormat(CurrencyFormatter.UnformatCurrency(value, formData.cultureCode, formData.currency), formData.cultureCode, formData.currency, 'en-US', 'USD', null, null, true);

            if (value == '' || value == 0) {
                value = "0%";
            }
        }

        if (name === 'paymentFrequency') {
            switch (value) {
                case '12':
                    setPaymentLabel(translator.translate('sandhills.yourestimatedmonthlypayment').toLowerCase());
                    break;
                case '4':
                    setPaymentLabel(translator.translate('sandhills.yourestimatedquarterlypayment').toLowerCase());
                    break;
                case '2':
                    setPaymentLabel(translator.translate('sandhills.yourestimatedbiannualpayment').toLowerCase());
                    break;
                case '1':
                    setPaymentLabel(translator.translate('sandhills.yourestimatedannualpayment').toLowerCase());
                    break;
            }
        }

        setFormData(previousState => {
            return { ...previousState, [name]: value };
        });

    }

    const currencyChange = (e) => {
        let newCurrency = e.target.options[e.target.selectedIndex].text;
        let newCulture = e.target.value;

        setFormData(previousState => {
            return {
                ...previousState,
                currency: newCurrency,
                cultureCode: newCulture,
                price: CurrencyFormatter.SwapCurrencyFormat(previousState.price, previousState.cultureCode, previousState.currency, newCulture, newCurrency),
                downPayment: CurrencyFormatter.SwapCurrencyFormat(previousState.downPayment, previousState.cultureCode, previousState.currency, newCulture, newCurrency, 0, 0)
            };
        });
    };

    return (
        <div id='calculator-form' className='calculator-form'>
            <div className='form-main-content'>
                <LoanApprovalEstimateFinCalc
                    {...props.LoanApprovalEstimateModel}
                    pageType='detail'
                    LenderUrl={props.LenderUrl}
                />
                <div class="overlay"></div>
                <div className='left-col'>
                    <TextField
                        name='price'
                        value={formData.price}
                        type='text'
                        class='form-el'
                        label={`Purchase Price (${formData.currency})`}
                        ref={(element) => inputReferences.current['price'] = element}
                        onFieldBlur={(e) => updateForm(e, true, false, true)}
                        onFieldChanged={updateForm}
                    />
                    <TextField
                        name='downPayment'
                        value={formData.downPayment}
                        type='text'
                        class='form-el two-col'
                        label={`Down Payment (${formData.currency})`}
                        ref={(element) => inputReferences.current['downPayment'] = element}
                        onFieldBlur={(e) => updateForm(e, true)}
                        onFieldChanged={updateForm}
                    />
                    <Slider
                        name='downPayment'
                        ref={(element) => inputReferences.current['downPaymentSlider'] = element}
                        aria-label='Down Payment'
                        label='Down Payment (USD)'
                        min={0}
                        defaultValue={0}
                        step={100}
                        size={'medium'}
                        value={CurrencyFormatter.UnformatCurrency(formData.downPayment, formData.cultureCode, formData.currency)}
                        max={CurrencyFormatter.UnformatCurrency(formData.price, formData.cultureCode, formData.currency)}
                        onChange={(e) => { updateForm(e, true) }}
                        sx={{ 'color': '#000' }}
                    />
                    <TextField
                        name='interest'
                        value={formData.interest}
                        type='text'
                        class='form-el two-col'
                        label={'Interest Rate'}
                        ref={(element) => inputReferences.current['interest'] = element}
                        onFieldBlur={(e) => { updateForm(e, false, true) }}
                        onFieldChanged={updateForm}
                    />
                    <Box sx={{ width: '100%' }} className='slider-container'>
                        <Slider
                            name='interest'
                            ref={(element) => inputReferences.current['interestSlider'] = element}
                            aria-label='Interest'
                            label='Interest'
                            defaultValue={'0'}
                            value={formData.interest.replace('%', '')}
                            step={1}
                            max={20}
                            min={0}
                            onChange={(e) => { updateForm(e, false, true) }}
                            sx={{ 'color': '#000' }}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography
                                variant="body2"
                                sx={{ cursor: 'pointer' }}
                            >
                                <span>0%</span>
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ cursor: 'pointer' }}
                            >
                                <span>20%</span>
                            </Typography>
                        </Box>
                    </Box>
                    <div className='bottom-options'>
                        <SelectField
                            name='paymentFrequency'
                            id='paymentFrequency'
                            class='form-el'
                            value={formData.paymentFrequency}
                            options={getPaymentFrequencyOptions()}
                            label={translator.translate('sandhills.paymentfrequency')}
                            ref={(element) => inputReferences.current['paymentFrequency'] = element}
                            onFieldChanged={updateForm}
                        />
                        <SelectField
                            id='termYears'
                            name='termYears'
                            value={formData.termYears}
                            type='number'
                            class='form-el'
                            label={'Loan Term'}
                            ref={(element) => inputReferences.current['termYears'] = element}
                            onFieldChanged={updateForm}
                            options={getLoanTermOptions()}
                        />
                    </div>
                    <div className='form-el ts-flexbox ts-justify-space-between'>
                        <SelectField
                            name='currency'
                            id='currency'
                            value={formData.cultureCode}
                            class={props.HideCurrencySelector ? 'visually-hidden' : 'form-el-flex-half'}
                            options={props.Currencies.map((currency) => { return { Text: currency.CurrencyCode, Value: currency.CultureCode }; })}
                            label={translator.translate('sandhills.currency')}
                            ref={(element) => inputReferences.current['currency'] = element}
                            onFieldChanged={currencyChange}
                        />
                    </div>
                </div>
                <div className='right-col calc-output'>
                    <div class="finance-box alt">
                        <div class="finance-total">
                            <span>{paymentLabel}</span>
                            <strong>{calculatedFields.payments}</strong>
                        </div>
                        <div>
                            <span>{translator.translate('sandhills.totalrepayment')} </span>
                            <strong>{calculatedFields.totalAmount}</strong>
                        </div>
                    </div>
                    {!isRVU &&
                        <div style={{ alignContent: 'center' }}>
                            <a className={props.LenderCompanyName == "Currency" ? 'calculator__finance-button currency' : 'calculator__finance-button'} href={props.LenderUrl} target='_blank' id='finance-widget'>
                                <img className='calculator__image' id='finance-widget-icon' src={props.LenderImageUrl} alt='Financing' />
                                <span className='calculator__text'>{translator.translate('sandhills.tradesites.mvc.applynow')?.toLowerCase()}</span>
                                <span className='visually-hidden'>({translator.translate('sandhills.opensinanewtab')})</span>
                            </a>
                        </div>
                    }
                </div>
            </div>
            {!isRVU &&
                <LoanApprovalEstimateBanner
                    isFinCalc={true}
                    {...props.LoanApprovalEstimateModel}
                    pageType='detail'
                    LenderUrl={props.LenderUrl}
                />
            }
            <div className='calculator-disclaimer'>
                <p>{translator.translate("sandhills.financialcalculatordisclaimer")}</p>
            </div>
        </div>
    );
};

export default CalculatorForm;